import React, { useEffect, useState, useRef } from "react";
import sobranieGif from '../assets/sobranie.gif';

/**
 * 🎮 FUTURATHON PASSWORD GAME
 * Terminal-style password game with level progression
 */

// 🔐 LEVELS - Edit these to change questions/answers
const levels = [
  {
    id: 1,
    clue: "Како се вика нашиот хакатон (на англиски)?",
    answer: "FUTURATHON",
    hint: "F..."
  },
  {
    id: 2,
    clue: "Каде се одржува хакатонот?",
    answer: "ITLABS",
    hint: "IT..."
  },
  {
    id: 4,
    clue: "Кога е настанот? (формат 20-21.12.2025)",
    answer: "20-21122025",
    hint: "20..."
  },
  {
    id: 5,
    clue: "Нашата мисија: да создадеме подобра ______.",
    answer: "ИДНИНА",
    hint: "И..."
  }
];

function PasswordGuess() {
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [isCorrect, setIsCorrect] = useState(false);
  const [isError, setIsError] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [specialConfirm, setSpecialConfirm] = useState(null); // { key: 'SOBRANIE' }
  const [showSpecialMessage, setShowSpecialMessage] = useState(false);
  const [showGif, setShowGif] = useState(false);
  const inputRef = useRef(null);

  const currentLevel = levels[currentLevelIndex];
  const isLastLevel = currentLevelIndex === levels.length - 1;

  // Normalize input: remove spaces, dots, uppercase
  const normalizeInput = (str) => {
    if (!str) return '';

    // If input contains Cyrillic, transliterate to Latin first
    const hasCyr = /\p{Script=Cyrillic}/u.test(str);
    let s = str;
    if (hasCyr) {
      const map = {
        'А':'A','Б':'B','В':'V','Г':'G','Д':'D','Ѓ':'GJ','Е':'E','Ж':'ZH','З':'Z','Ѕ':'DZ',
        'И':'I','Ј':'J','К':'K','Л':'L','Љ':'LJ','М':'M','Н':'N','Њ':'NJ','О':'O','П':'P',
        'Р':'R','С':'S','Т':'T','Ќ':'KJ','У':'U','Ф':'F','Х':'H','Ц':'C','Ч':'CH','Џ':'DZ','Ш':'SH',
        'а':'a','б':'b','в':'v','г':'g','д':'d','ѓ':'gj','е':'e','ж':'zh','з':'z','ѕ':'dz',
        'и':'i','ј':'j','к':'k','л':'l','љ':'lj','м':'m','н':'n','њ':'nj','о':'o','п':'p',
        'р':'r','с':'s','т':'t','ќ':'kj','у':'u','ф':'f','х':'h','ц':'c','ч':'ch','џ':'dz','ш':'sh'
      };

      let out = '';
      for (const ch of s) {
        out += map[ch] !== undefined ? map[ch] : ch;
      }
      s = out;
    }

    // Normalize unicode (remove diacritics), then remove any non-letter/number characters
    const cleaned = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return cleaned.replace(/[^\p{L}\p{N}]/gu, '').toUpperCase();
  };

  // Auto-focus input when level changes
  useEffect(() => {
    if (inputRef.current && !gameCompleted) {
      inputRef.current.focus();
    }
  }, [currentLevelIndex, gameCompleted]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!userInput.trim() || gameCompleted) return;

    const normalizedInput = normalizeInput(userInput);
    const normalizedAnswer = normalizeInput(currentLevel.answer);

    // Special keywords trigger the "sobranie" flow
    const specialKeys = ['SOBRANIE', 'BORD', 'NOVBORD'];
    const matchedSpecial = specialKeys.find(k => normalizedInput.includes(k));
    if (matchedSpecial) {
      // Show confirmation modal before playing/continuing
      setSpecialConfirm({ key: matchedSpecial });
      return;
    }

    if (normalizedInput === normalizedAnswer) {
      // ✅ CORRECT
      setIsCorrect(true);
      setIsError(false);
      setWrongAttempts(0);
      setShowHint(false);

      // Advance to next level or complete game
      setTimeout(() => {
        if (isLastLevel) {
          setGameCompleted(true);
        } else {
          setCurrentLevelIndex(prev => prev + 1);
          setUserInput('');
          setIsCorrect(false);
        }
      }, 1500);
    } else {
      // ❌ INCORRECT
      setIsError(true);
      setWrongAttempts(prev => prev + 1);
      
      // Show hint after 3 wrong attempts
      if (wrongAttempts >= 2) {
        setShowHint(true);
      }

      // Reset error animation
      setTimeout(() => setIsError(false), 600);
    }
  };

  const handleRestart = () => {
    setCurrentLevelIndex(0);
    setUserInput('');
    setIsCorrect(false);
    setIsError(false);
    setShowHint(false);
    setWrongAttempts(0);
    setGameCompleted(false);
  };

  const registrationLink = 'https://docs.google.com/forms/d/e/1FAIpQLSf6p-Cd071IJkln9JH2ZH535XMKNSCfqu1_2Koq6JIxkrnLhA/viewform';

  const handleSpecialChoice = (confirm) => {
          if (!specialConfirm) return;
          setSpecialConfirm(null);
          if (confirm) {
            // Show gif overlay (bundled asset) and keep it until user closes
            setShowGif(true);
            // clear input so user can continue
            setUserInput('');
          }
  };

  if (gameCompleted) {
    return (
      <div style={{ margin: '36px auto', maxWidth: 720 }}>
        <div style={{ 
          background: '#6A310A', 
          borderRadius: 24, 
          padding: '48px 32px', 
          border: '2px solid #EFA74F',
          boxShadow: '0 0 30px rgba(239, 167, 79, 0.3)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Scanline effect */}
          <div style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `repeating-linear-gradient(0deg, rgba(255,255,255,0.03), rgba(255,255,255,0.03) 1px, transparent 1px, transparent 2px)`,
            opacity: 0.2
          }} />
          
          <div style={{ position: 'relative', zIndex: 10, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* Success badge */}
            <div style={{ display: 'inline-block', padding: '8px 24px', background: '#749269', borderRadius: 9999, border: '1px solid #EFA74F', margin: '0 auto' }}>
              <span style={{ color: '#DFC293', fontWeight: 700, letterSpacing: 1.5, fontSize: '0.875rem' }}>TERMINAL ACCESS</span>
            </div>

            {/* Main message */}
            <h1 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#EFA74F', letterSpacing: 2, textShadow: '0 0 10px rgba(239, 167, 79, 0.5)' }}>
              ACCESS GRANTED
            </h1>
            
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#DFC293' }}>
              FUTURATHON UNLOCKED
            </div>

            {/* Message */}
            <p style={{ fontSize: '1.125rem', color: '#DFC293', lineHeight: 1.6, maxWidth: 480, margin: '16px auto' }}>
              Сега ти си на ред да создадеш нешто за подобра иднина.
            </p>

            {/* CTA Button */}
            <div style={{ paddingTop: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <a 
                href="https://docs.google.com/forms/d/e/1FAIpQLSf6p-Cd071IJkln9JH2ZH535XMKNSCfqu1_2Koq6JIxkrnLhA/viewform"
                target="_blank"
                rel="noopener noreferrer"
                style={{ 
                  display: 'inline-block', 
                  padding: '16px 40px', 
                  background: '#EFA74F', 
                  color: '#6A310A', 
                  fontWeight: 700, 
                  fontSize: '1.25rem', 
                  borderRadius: 12,
                  textDecoration: 'none',
                  transition: 'all 0.3s',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}
              >
                Пријави се на хакатонот
              </a>
              
              <button
                onClick={handleRestart}
                style={{ 
                  padding: '8px 24px', 
                  color: '#749269', 
                  border: '1px solid #749269', 
                  borderRadius: 8,
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                Играј повторно
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: '36px auto', maxWidth: 720 }}>
      <div 
        className={isError ? 'password-game-shake' : ''} 
        style={{ 
          background: '#6A310A', 
          borderRadius: 24, 
          padding: '32px 24px', 
          border: isCorrect ? '2px solid #749269' : '2px solid #EFA74F',
          boxShadow: isCorrect ? '0 0 30px rgba(116, 146, 105, 0.5)' : '0 0 20px rgba(239, 167, 79, 0.3)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Scanline overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `repeating-linear-gradient(0deg, rgba(255,255,255,0.03), rgba(255,255,255,0.03) 1px, transparent 1px, transparent 2px)`,
          opacity: 0.2
        }} />
        
        <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'inline-block', padding: '8px 16px', background: '#749269', borderRadius: 9999, border: '1px solid #EFA74F' }}>
              <span style={{ color: '#DFC293', fontWeight: 700, letterSpacing: 1.5, fontSize: '0.75rem' }}>ACCESS TERMINAL</span>
            </div>
            
            <div style={{ color: '#EFA74F', fontWeight: 700, fontSize: '1.125rem' }}>
              LEVEL {currentLevel.id} / {levels.length}
            </div>
          </div>

          {/* Clue */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ color: '#749269', fontSize: '0.875rem', fontWeight: 600, letterSpacing: 1.5 }}>SECURITY QUESTION:</div>
            <div style={{ color: '#DFC293', fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.6 }}>
              {currentLevel.clue}
            </div>
          </div>

          {/* Hint, input and related UI - hide while confirming or when GIF is shown */}
          {!specialConfirm && !showGif && showHint && (
            <div style={{ background: 'rgba(228, 87, 11, 0.2)', border: '1px solid #EFA74F', borderRadius: 8, padding: 12 }}>
              <div style={{ color: '#EFA74F', fontSize: '0.875rem', fontWeight: 600 }}>💡 HINT:</div>
              <div style={{ color: '#DFC293' }}>{currentLevel.hint}</div>
            </div>
          )}

          {!specialConfirm && !showGif && (
            <>
              {/* Input form */}
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#749269', fontFamily: 'monospace', fontSize: '1.125rem' }}>
                    &gt;
                  </div>
                  <input
                    ref={inputRef}
                    type="text"
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Внеси го одговорот..."
                    disabled={isCorrect}
                    style={{ 
                      width: '100%', 
                      paddingLeft: 40, 
                      paddingRight: 16, 
                      paddingTop: 16, 
                      paddingBottom: 16, 
                      background: 'rgba(0,0,0,0.4)', 
                      border: '2px solid #749269',
                      borderRadius: 12,
                      color: '#EFA74F',
                      fontSize: '1.125rem',
                      fontFamily: 'monospace',
                      outline: 'none'
                    }}
                  />
                  {!userInput && (
                    <div className="password-game-blink" style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', width: 8, height: 24, background: '#EFA74F' }} />
                  )}
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isCorrect || !userInput.trim()}
                  className="signup-btn"
                  style={{ 
                    width: '100%', 
                    padding: 16, 
                    background: isCorrect ? '#749269' : '#EFA74F', 
                    color: '#6A310A', 
                    fontWeight: 700, 
                    fontSize: '1.125rem', 
                    borderRadius: 12,
                    border: 'none',
                    cursor: isCorrect || !userInput.trim() ? 'not-allowed' : 'pointer',
                    opacity: isCorrect || !userInput.trim() ? 0.5 : 1
                  }}
                >
                  {isCorrect ? '✓ ACCESS GRANTED' : 'SUBMIT'}
                </button>
              </form>

              {/* Error message */}
              {isError && (
                <div style={{ textAlign: 'center', color: '#ff6b6b', fontWeight: 700, fontSize: '1.125rem' }}>
                  ❌ WRONG PASSWORD – TRY AGAIN
                </div>
              )}
            </>
          )}

          {/* Special registration message shown after confirming sobranie flow */}
          {showSpecialMessage && (
            <div style={{ marginTop: 12, background: '#6A310A', border: '1px solid #EFA74F', padding: 12, borderRadius: 8, color: '#DFC293' }}>
              <div style={{ fontWeight: 700, color: '#EFA74F', marginBottom: 8 }}>Што чекаш? Пријави се!</div>
              <a href={registrationLink} target="_blank" rel="noopener noreferrer" style={{ color: '#EFA74F', fontWeight: 700 }}>
                Пријави се овде
              </a>
            </div>
          )}

          {/* Confirmation modal for special keywords */}
          {specialConfirm && (
            <div className="fixed inset-0 z-60 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
              <div style={{ background: '#6A310A', padding: 20, borderRadius: 12, border: '2px solid #EFA74F', maxWidth: 480, width: '90%', color: '#DFC293' }}>
                <h3 style={{ color: '#EFA74F', fontSize: '1.25rem', marginBottom: 8 }}>Дали си сигурен?</h3>
                <p style={{ marginBottom: 12 }}>Ти побара да се пушти "sobranie" — дали сакаш да продолжиш?</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => handleSpecialChoice(false)} style={{ padding: '8px 12px', borderRadius: 8, background: '#6A310A', color: '#DFC293', border: '1px solid #749269' }}>Не</button>
                  <button onClick={() => handleSpecialChoice(true)} style={{ padding: '8px 12px', borderRadius: 8, background: '#EFA74F', color: '#6A310A', border: 'none', fontWeight: 700 }}>Да</button>
                </div>
                <div style={{ marginTop: 12, borderTop: '1px dashed rgba(223,194,147,0.1)', paddingTop: 12 }}>
                  <div style={{ color: '#EFA74F', fontWeight: 700 }}>Што чекаш? Пријави се!</div>
                  <a href={registrationLink} target="_blank" rel="noopener noreferrer" style={{ color: '#EFA74F' }}>Пријави се овде</a>
                </div>
              </div>
            </div>
          )}

          {/* GIF overlay shown when user confirms special flow */}
          {showGif && (
            <div className="fixed inset-0 z-70 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
              <div style={{ position: 'absolute', right: 12, top: 12, zIndex: 80 }}>
                <button
                  onClick={() => { setShowGif(false); setShowSpecialMessage(true); }}
                  aria-label="Close"
                  title="Затвори"
                  style={{
                    width: 36,
                    height: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.48)',
                    borderRadius: 9999,
                    border: '1px solid rgba(239,167,79,0.12)',
                    cursor: 'pointer',
                    padding: 0,
                    color: '#EFA74F'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EFA74F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div style={{ maxWidth: '90%', maxHeight: '90%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={sobranieGif} alt="sobranie" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }} onError={() => { setShowGif(false); setShowSpecialMessage(true); }} />
              </div>
            </div>
          )}

          {/* Progress indicator - hide during special flow/GIF */}
          {!specialConfirm && !showGif && (
            <div style={{ paddingTop: 16 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {levels.map((_, idx) => (
                  <div
                    key={idx}
                    className={idx === currentLevelIndex ? 'password-game-pulse' : ''}
                    style={{ 
                      height: 8, 
                      flex: 1, 
                      borderRadius: 9999,
                      background: idx < currentLevelIndex ? '#749269' : idx === currentLevelIndex ? '#EFA74F' : '#6A310A',
                      border: idx >= currentLevelIndex ? '1px solid #749269' : 'none',
                      transition: 'all 0.5s'
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes passwordGameBlink {
          0%, 49% { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        .password-game-blink {
          animation: passwordGameBlink 1s infinite;
        }

        @keyframes passwordGameShake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .password-game-shake {
          animation: passwordGameShake 0.4s ease-in-out;
        }

        @keyframes passwordGamePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .password-game-pulse {
          animation: passwordGamePulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

const FuturathonLanding = () => {
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const countdown = () => {
      const endDate = new Date("December 21, 2025 00:00:00").getTime();
      const now = Date.now();
      const distance = endDate - now;

      const days = Math.max(0, Math.floor(distance / (1000 * 60 * 60 * 24)));
      const hours = Math.max(0, Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
      const minutes = Math.max(0, Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)));
      const seconds = Math.max(0, Math.floor((distance % (1000 * 60)) / 1000));

      setTime({ days, hours, minutes, seconds });
    };

    countdown();
    const id = setInterval(countdown, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="futurathon-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700&display=swap');

        .futurathon-root {
          margin: 0;
          padding: 0;
          font-family: 'Orbitron', Arial, sans-serif;
          background-color: #e4570B;
          color: #DFC293;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 100vh;
          overflow-x: hidden;
          position: relative;
        }

        .futurathon-root::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background-image: linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 40px 40px;
          z-index: 0;
          pointer-events: none;
        }

        .fade-in { opacity: 0; animation: fadeIn 1.5s ease forwards; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

        .hero { margin-top: 80px; width: 90%; max-width: 900px; text-align: center; z-index: 1; position: relative; }

        .title-box { background-color: #6A310A; padding: 50px 30px; margin: 0 auto 40px; border-radius: 14px; width: 70%; max-width: 650px; min-width: 300px; box-shadow: 0 0 30px rgba(239,167,79,0.7); border: 2px solid #EFA74F; }
        .title-box h1 { color: #EFA74F; font-size: 3.6em; margin: 0; letter-spacing: 2px; text-shadow: 0 0 15px rgba(239,167,79,0.7); word-wrap: break-word; }

        .signup-btn { background-color: #749269; color: #DFC293; border: none; padding: 18px 50px; font-size: 1.5em; border-radius: 12px; text-decoration: none; font-weight: bold; cursor: pointer; transition: all 0.3s ease; box-shadow: 0 6px 20px rgba(0,0,0,0.35); display: inline-block; margin-bottom: 30px; z-index: 1; position: relative; }
        .signup-btn:hover { background-color: #87a882; color: #EFA74F; transform: translateY(-3px); box-shadow: 0 8px 25px rgba(0,0,0,0.45); }

        .tagline-box { background-color: #6A310A; padding: 30px 25px; margin: 25px auto 20px; border-radius: 12px; width: 65%; max-width: 620px; box-shadow: 0 0 20px rgba(239,167,79,0.6); border: 2px solid #EFA74F; }
        .tagline { font-size: 1.7em; color: #EFA74F; margin: 0; font-weight: 500; text-shadow: 0 0 12px rgba(239,167,79,0.5); }

        .countdown-box { background-color: #6A310A; padding: 25px 30px; margin: 20px auto 50px; border-radius: 12px; width: 60%; max-width: 550px; box-shadow: 0 0 20px rgba(239,167,79,0.6); border: 2px solid #EFA74F; }
        #countdown { font-size: 1.5em; color: #EFA74F; text-shadow: 0 0 12px rgba(239,167,79,0.6); font-weight: 600; display: flex; justify-content: center; gap: 18px; align-items: center; }
        .count-unit { display: flex; flex-direction: column; align-items: center; gap: 6px; }
        .count-value { font-size: 1.6em; font-weight: 700; }
        .count-label { font-size: 0.8em; opacity: 0.95; }

        .description-card { background-color: rgba(0,0,0,0.20); padding: 50px 40px; border-radius: 16px; width: 85%; max-width: 900px; margin-bottom: 120px; box-shadow: 0 0 35px rgba(0,0,0,0.35); border: 1px solid rgba(255,255,255,0.18); animation-delay: 0.2s; z-index: 1; position: relative; }
        .description-card p { font-size: 1.28em; line-height: 1.8; color: #F3D9B6; margin: 0; }

        .shape { position: absolute; border-radius: 50%; opacity: 0.12; animation: float 12s infinite alternate ease-in-out; pointer-events: none; }
        .shape1 { width: 100px; height: 100px; background-color: #EFA74F; top: 10%; left: 5%; animation-delay: 0s; }
        .shape2 { width: 150px; height: 150px; background-color: #749269; top: 70%; left: 10%; animation-delay: 3s; }
        .shape3 { width: 80px; height: 80px; background-color: #6A310A; top: 50%; left: 80%; animation-delay: 1s; }
        .shape4 { width: 120px; height: 120px; background-color: #EFA74F; top: 20%; left: 75%; animation-delay: 2s; }

        @keyframes float { from { transform: translateY(0px) translateX(0px); } to { transform: translateY(-25px) translateX(20px); } }

        @media (max-width: 768px) {
          .title-box { padding: 30px 20px; width: 85%; min-width: unset; }
          .title-box h1 { font-size: 2.2em; letter-spacing: 1px; }
          .signup-btn { font-size: 1.3em; padding: 14px 38px; }
          .tagline { font-size: 1.5em; }
          .description-card p { font-size: 1.15em; }
          #countdown { font-size: 1.2em; flex-direction: column; gap: 10px; }
        }
        
        @media (max-width: 480px) {
          .title-box { padding: 25px 15px; width: 90%; }
          .title-box h1 { font-size: 1.8em; letter-spacing: 0.5px; }
          .tagline-box { width: 85%; padding: 20px 15px; }
          .tagline { font-size: 1.3em; }
          .signup-btn { font-size: 1.2em; padding: 12px 30px; }
        }
      `}</style>

      <div className="shape shape1" />
      <div className="shape shape2" />
      <div className="shape shape3" />
      <div className="shape shape4" />

      <div className="hero fade-in">
        <div className="title-box fade-in">
          <h1>FUTURATHON 2025</h1>
        </div>

        <a href="https://docs.google.com/forms/d/e/1FAIpQLSf6p-Cd071IJkln9JH2ZH535XMKNSCfqu1_2Koq6JIxkrnLhA/viewform" className="signup-btn fade-in" style={{ animationDelay: "0.15s" }}>Пријави се!</a>

        <div className="tagline-box fade-in" style={{ animationDelay: "0.25s" }}>
          <div className="tagline">The future belongs to those who dare to hack it.</div>
        </div>

        <div className="countdown-box fade-in" style={{ animationDelay: "0.3s" }}>
          <div id="countdown">
            <div className="count-unit">
              <span className="count-value" id="days">{time.days}</span>
              <span className="count-label">Days</span>
            </div>

            <div className="count-unit">
              <span className="count-value" id="hours">{time.hours}</span>
              <span className="count-label">Hours</span>
            </div>

            <div className="count-unit">
              <span className="count-value" id="minutes">{time.minutes}</span>
              <span className="count-label">Minutes</span>
            </div>

            <div className="count-unit">
              <span className="count-value" id="seconds">{time.seconds}</span>
              <span className="count-label">Seconds</span>
            </div>
          </div>
        </div>
        <div className="fade-in" style={{ animationDelay: '0.36s' }}>
          <PasswordGuess />
        </div>
      </div>

      {/* <div className="description-card fade-in" style={{ animationDelay: "0.35s" }}>
        <p>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer aliquam ultrices magna, nec elementum
          nunc. Donec fringilla odio nec justo scelerisque, a pulvinar odio varius. Aliquam erat volutpat. Morbi
          ac volutpat ex. Cras a sapien a augue consequat consequat. Pellentesque habitant morbi tristique
          senectus et netus et malesuada fames ac turpis egestas. Vivamus facilisis, massa id feugiat pretium,
          sapien nunc euismod tortor, eget dictum lorem nulla at magna.
        </p>
      </div> */}
    </div>
  );
};

export default FuturathonLanding;
